import moment from 'moment';
import { merge, range, isNil } from 'lodash';
import React, { PropTypes } from 'react';
import SimpleDropDownMenu from './SimpleDropDownMenu';

const rexMonthYear = /^(0?[1-9]|1?[0-2])\/([0-9]{2}){1,2}$/g;
const rexYear = /^([0-9]{2}){1,2}$/g;

export default class DateInputDropDown extends React.Component {
  static propTypes = {
    onBlur : PropTypes.func,
    onChange : PropTypes.func,
    dateFormat : PropTypes.oneOf(['Full', 'MonthYear', 'Year']),
    defaultValue : PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date)
    ]),
    value : PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date)
    ]),
    floatingLabelText: PropTypes.string,
    hintText : PropTypes.string,
    errorText : PropTypes.string,
    includeFuture: PropTypes.bool,
    minDate: PropTypes.object,
    includeDefault: PropTypes.bool,
    'data-id': PropTypes.string,
    labelStyle: PropTypes.object,
    underlineStyle: PropTypes.object,
    iconStyle: PropTypes.object,
  }

  static defaultProps = {
    dateFormat : 'Full'
  }

  constructor(props, context) {
    super(props, context);

    const dateValue = this.normalizeValue(props.value || props.defaultValue);
    const textValue = this.formatDateString(dateValue, props.dateFormat);

    const monthValue = moment(dateValue).format('M');
    const yearValue = moment(dateValue).format('YYYY');

    this.state = {
      dateValue,
      textValue,
      monthValue,
      yearValue,
      monthArray: range(1, 13),
      yearArray: range(moment().add(props.includeFuture ? 10 : 0, 'year').format('YYYY'), 1940, -1),
      isValid: true,
      isFuture: true,
    };
  }

  componentWillReceiveProps(nextProps) {
    // clear dropdowns if value reset to null.
    if(nextProps.hasOwnProperty('value') && isNil(nextProps.value)) {
      this.setState({ monthValue: null, yearValue: null });
    }
  }

  normalizeValue(value) {
    if (typeof value === 'string') {
      if (value.match(rexMonthYear)) {
        const [month, year] = value.split('/');
        return new Date(year, parseInt(month) - 1); // Jan = 0, Dec = 11
      }
      else if (value.match(rexYear)) {
        return new Date(value, 0); // Jan = 0
      }
      else if (moment(value).isValid()) {
        return new Date(value);
      }
    }
    else if (value instanceof Date) {
      return value;
    }

    return null;
  }

  formatDateString(value, format) {
    if (!moment(value).isValid()) {
      return undefined;
    }

    switch (format) {
      case 'Year':
        return moment(value).format('YYYY');

      case 'MonthYear':
        return moment(value).format('MM/YYYY');

      default:
        return moment(value).format('L');

    }
  }

  isValidFormat(stringDate) {
    return (this.props.dateFormat === 'Year') ? !!stringDate.match(rexYear) :
            (this.props.dateFormat === 'MonthYear') ? !!stringDate.match(rexMonthYear) :
             (moment(stringDate, ['MM/DD/YYYY', 'MM/DD/YY', 'MM/D/YYYY', 'M/DD/YYYY', 'M/D/YYYY', 'M/D/YY'], true).isValid());
  }

  handleTextChange = (event, index, value) => {
    const newState = merge({}, this.state);
    newState.textValue = `${this.monthDropdown.state.value}/${this.yearDropdown.state.value}`;
    newState.monthValue = this.monthDropdown.state.value;
    newState.yearValue = this.yearDropdown.state.value;

    newState.isValid = this.isValidFormat(newState.textValue);
    newState.dateValue = (newState.isValid) ? this.normalizeValue(newState.textValue) : null;

    // if minDate provided, we need to validate against the minDate.
    newState.isFuture = true;
    if (this.props.minDate && newState.isValid) {
      newState.isValid = newState.isFuture = moment(newState.dateValue).isSameOrAfter(this.props.minDate, 'day');
      newState.dateValue = newState.isValid ? newState.dateValue : null;
    }

    if (this.props.onChange) {
      this.props.onChange(Object.assign({}, event, {target: {value: newState.dateValue}}));
    }
    this.setState(newState);
  }

  handleBlur = (event) => {
    if (this.props.onBlur) {
      this.props.onBlur(Object.assign({}, event, {target: {value: this.state.dateValue}}));
    }
  }

  render() {
    const {
      className,
      errorText,
      autoOk,
      includeDefault,
      floatingLabelText,
      hintText,
      isDirty,
      minDate,
      labelStyle,
      underlineStyle,
      iconStyle,
      disabled,
      style,
      ...otherProps
    } = this.props;

    const defaultErrorMessage = !this.state.isValid ? 'Invalid date format.' : undefined;

    const defaultClassName = className || 'date-input';

    const errorStr = errorText || defaultErrorMessage;

    let errorMessage;

    const isError = isDirty && (typeof errorStr !== 'undefined' || this.state.errors.length);

    // handle error state visually
    if(isError) {
      errorMessage = <div className='error-message'>{errorStr}</div>;
    }

    // if minDate provided, we need to validate against the minDate.
    if (this.props.minDate && !this.state.isFuture) {
      errorMessage = <div className='error-message'>Please enter a date after From Date.</div>;
    }

    return (
      <div className={`${defaultClassName}-container`}>
        <div className='text-prompt-left'>
          {floatingLabelText}
        </div>
        <div className='date-input-dropdowns'>
            <SimpleDropDownMenu className='date-month-dropdown'
              className={`${defaultClassName}-month`}
              hintText='Month'
              ref={(input) => { this.monthDropdown = input; }}
              canAutoPosition
              includeDefault
              data-id={`${this.props['data-id']}-month`}
              onChange={this.handleTextChange}
              onBlur={this.handleBlur}
              value={parseInt(this.state.monthValue) || ''}
              primaryText='Month'
              menuItems={this.state.monthArray}
              labelStyle={labelStyle}
              underlineStyle={underlineStyle}
              iconStyle={iconStyle}
              disabled={disabled}
              style={style}
            />
            <SimpleDropDownMenu className='date-year-dropdown'
              className={`${defaultClassName}-year`}
              hintText='Year'
              ref={(input) => { this.yearDropdown = input; }}
              canAutoPosition
              includeDefault
              data-id={`${this.props['data-id']}-year`}
              onChange={this.handleTextChange}
              onBlur={this.handleBlur}
              value={parseInt(this.state.yearValue) || ''}
              primaryText='Year'
              menuItems={this.state.yearArray}
              labelStyle={labelStyle}
              underlineStyle={underlineStyle}
              iconStyle={iconStyle}
              disabled={disabled}
              style={style}
            />
            {errorMessage}
          </div>
      </div>
    );
  }

}



// WEBPACK FOOTER //
// ./shared/common/components/DateInputDropDown.js